import { useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Point } from "./game/geometry";
import type { GalaxyViewportSize } from "./galaxy-viewport";

interface Props {
  id: string;
  pinned: boolean;
  size: GalaxyViewportSize;
  anchor: Point;
  clearance: number;
  onHold: () => void;
  onLeave: () => void;
  onDismiss: () => void;
  children: ReactNode;
}

export function GalaxyMapPopover({ id, pinned, size, anchor, clearance, onHold, onLeave, onDismiss, children }: Props) {
  const root = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState({ width: 288, height: 180 });
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      setBounds({ width: element.offsetWidth, height: element.offsetHeight });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const fitsRight = anchor.x + clearance + bounds.width <= size.width - 8;
  const fitsLeft = anchor.x - clearance - bounds.width >= 8;
  const left = fitsRight ? anchor.x + clearance : fitsLeft ? anchor.x - bounds.width - clearance : anchor.x - bounds.width / 2;
  const desiredTop = fitsRight || fitsLeft ? anchor.y - 32
    : anchor.y + clearance + bounds.height <= size.height - 8 ? anchor.y + clearance : anchor.y - clearance - bounds.height;
  const top = Math.max(8, Math.min(size.height - bounds.height - 8, desiredTop));
  return (
    <div id={id} ref={root} className={`galaxy-map-popover${pinned ? " pinned" : ""}`} role={pinned ? "dialog" : "tooltip"}
      aria-label={pinned ? "Выбранное место" : undefined} data-galaxy-ui
      style={{ left: Math.max(8, Math.min(size.width - bounds.width - 8, left)), top, maxWidth: Math.max(0, size.width - 16), maxHeight: Math.max(0, size.height - 16) }}
      onPointerEnter={onHold} onPointerLeave={onLeave}>
      {pinned && <button type="button" className="galaxy-tooltip-close" data-galaxy-focus aria-label="Скрыть подсказку" onClick={onDismiss}>×</button>}
      {children}
    </div>
  );
}
